import React from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Avatar,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Search,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateField } from "@mui/x-date-pickers/DateField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAuditoriaUtils } from "../hooks/useAuditoriaUtils";
import { useAuditoria } from "../contexts/AuditoriaContext";

const AppBarAuditoria = ({ allCentros }) => {
  const {
    selectedDate,
    selectedClinica,
    selectedOdontologo,
    odontologosLista,
    loading,
    updateAuditoria,
    auditoriaActivaFecha,
  } = useAuditoria();

  const {
    handleSearch,
    handlePrevMonth,
    handleNextMonth,
    handleDateChange,
    filtrarOdontologosPorClinica,
  } = useAuditoriaUtils();

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        p: 1,
        backgroundColor: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
      }}
    >
      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
        {"Auditoría - Período activo: " +
          (auditoriaActivaFecha
            ? dayjs(auditoriaActivaFecha).format("MM/YYYY")
            : "No disponible")}
      </Typography>

      {/* Selector de mes */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button onClick={handlePrevMonth}>
          <KeyboardArrowLeft sx={{ fontSize: "2rem" }} />
        </Button>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <DateField
            value={dayjs(selectedDate)}
            inputFormat="MM YYYY"
            format="MM YYYY"
            onChange={handleDateChange}
            size="small"
            sx={{ width: 100 }}
          />
        </LocalizationProvider>
        <Button onClick={handleNextMonth}>
          <KeyboardArrowRight sx={{ fontSize: "2rem" }} />
        </Button>
      </Box>

      {/* Clínica */}
      <Autocomplete
        id="clinica"
        sx={{ width: 190 }}
        options={allCentros || []}
        value={selectedClinica}
        autoHighlight
        size="small"
        onChange={(event, newValue) => {
          if (!newValue) {
            updateAuditoria({
              selectedClinica: null,
              selectedOdontologo: null,
              auditingRows: [],
              odontologosLista: [],
            });
            return;
          }
          updateAuditoria({
            selectedClinica: newValue,
            selectedOdontologo: null,
            auditingRows: [],
          });
          const odontos = filtrarOdontologosPorClinica(newValue.id);
          updateAuditoria({ odontologosLista: odontos });
        }}
        getOptionLabel={(option) => option.razonsocial}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: option.color,
                mr: 1,
                border: 1,
                borderColor: "black",
              }}
            >
              {option.identificador}
            </Avatar>
            {option.razonsocial}
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Clínica" variant="outlined" />
        )}
      />

      {/* Odontólogo */}
      <Autocomplete
        id="odontologo"
        sx={{ width: 300 }}
        options={[...(odontologosLista || [])].sort((a, b) =>
          a.odontologo.localeCompare(b.odontologo)
        )}
        value={selectedOdontologo}
        autoHighlight
        size="small"
        onChange={(event, newValue) =>
          updateAuditoria({
            selectedOdontologo: newValue ?? null,
            auditingRows: [],
          })
        }
        getOptionLabel={(option) =>
          option?.odontologo
            ?.toLowerCase()
            .split(" ")
            .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
            .join(" ") || ""
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField {...params} label="Odontólogo" variant="outlined" />
        )}
      />

      {/* Buscar */}
      <LoadingButton
        size="medium"
        onClick={handleSearch}
        loading={loading}
        loadingPosition="start"
        endIcon={<Search />}
        sx={{
          backgroundColor: "primary.main",
          ":hover": { bgcolor: "#475569", color: "white" },
          width: 80,
        }}
        variant="contained"
      >
        <span></span>
      </LoadingButton>
    </Box>
  );
};

export default AppBarAuditoria;
